/**
 * Redux slice des rapprochements (chantier C31 « Rapprocher les concepts », SP1
 * « Les paires ») : les paires de concepts à rapprocher dans la portée choisie,
 * telles que le serveur les rend, et les paires écartées.
 *
 * Minimal : une lecture remplace le résultat ; écarter retire la paire de la liste
 * et pose l'écart ; reprendre retire l'écart (la paire remonte à la lecture suivante) ;
 * calculer les empreintes relance la lecture. Garde `requestId` sur la lecture
 * (une réponse périmée est ignorée). Les messages d'échec du serveur remontent
 * tels quels, avec leur `code` (409 `PORTEE_TROP_GRANDE`).
 *
 * SP2 « Réunir » : `apercuReunion` lit les conséquences en chiffres (rien dans l'état :
 * la fenêtre les tient) ; `reunirPaire` réunit A dans B et, accompli, retire de la liste
 * TOUTE paire qui porte A (A n'existe plus) et pose `dernierBilan`.
 *
 * C31 bis SP2 « Réunir par dossier » : les deux thunks passent `dossierId` ; un bilan
 * `reuni.survit` ne retire ni paire ni voisin (A existe, ses raisons sont au serveur —
 * `RapprocherModal` relit les paires), il pose seulement `dernierBilan`.
 *
 * SP3 « Distinguer et voisins » : `listerVoisins` lit les voisins du concept ouvert sur sa
 * fiche (`voisins`, un seul concept à la fois, garde `requestId` à part) ; un écart ou une
 * réunion retire le voisin concerné sans relecture ; un échec vide les voisins sans
 * toucher au reste de l'état (la fiche n'en dépend pas).
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { rapprochementService } from '../../services/rapprochementAPI';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import type { ApercuReunion, BilanReunion, BilanScission, EcartDto, PassageAScinder, PorteeRapprochement, ResultatRapprochements, ResultatVoisins } from '../../types/api';

export type RapprochementsStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface EchecRapprochement { message: string; code?: string }

export interface RapprochementsState {
    status: RapprochementsStatus;
    portee: PorteeRapprochement;
    resultat: ResultatRapprochements | null;
    ecarts: EcartDto[];
    erreur?: string;
    /** Code du serveur quand il y en a un (`PORTEE_TROP_GRANDE`). */
    erreurCode?: string;
    calculEnCours: boolean;
    requestId: string | null;
    /** Le bilan de la dernière réunion accomplie (SP2), pour le bandeau. */
    dernierBilan: BilanReunion | null;
    /** Les voisins du concept ouvert sur sa fiche (SP3) ; `null` tant qu'ils ne sont pas lus. */
    voisins: ResultatVoisins | null;
    voisinsRequestId: string | null;
}

export const PORTEE_PAR_DEFAUT: PorteeRapprochement = { sorte: 'tous' };

export const initialRapprochementsState: RapprochementsState = {
    status: 'idle', portee: PORTEE_PAR_DEFAUT, resultat: null, ecarts: [], calculEnCours: false, requestId: null, dernierBilan: null,
    voisins: null, voisinsRequestId: null,
};

const AUCUN_ECART: EcartDto[] = Object.freeze([]) as unknown as EcartDto[];

async function tokenOf(instance: any): Promise<string> {
    const accessToken = await getTokenRedirect(tokenRequest, instance);
    if (!accessToken) throw new Error('Impossible d\'obtenir le token d\'accès');
    return accessToken;
}

/** Message et code d'un échec axios : ceux du serveur, sinon générique. */
function echecDe(error: any, fallback: string): EchecRapprochement {
    const message = error?.response?.data?.message || error?.message || fallback;
    const code = error?.response?.data?.code;
    return typeof code === 'string' ? { message, code } : { message };
}

/** Clé d'un couple, dans l'ordre : la même quel que soit l'ordre d'entrée. */
export const cleDuCouple = (a: string, b: string): string => (a < b ? `${a}\u0000${b}` : `${b}\u0000${a}`);

export const listerRapprochements = createAsyncThunk<ResultatRapprochements, { instance: any; portee: PorteeRapprochement }, { rejectValue: EchecRapprochement }>(
    'rapprochements/lister',
    async ({ instance, portee }, { rejectWithValue }) => {
        try {
            return await rapprochementService.lister(portee, await tokenOf(instance));
        } catch (error) {
            return rejectWithValue(echecDe(error, 'Lecture impossible. Réessayez.'));
        }
    },
);

export const listerEcarts = createAsyncThunk<EcartDto[], { instance: any }, { rejectValue: EchecRapprochement }>(
    'rapprochements/listerEcarts',
    async ({ instance }, { rejectWithValue }) => {
        try {
            return await rapprochementService.ecarts(await tokenOf(instance));
        } catch (error) {
            return rejectWithValue(echecDe(error, 'Lecture impossible. Réessayez.'));
        }
    },
);

export const ecarterPaire = createAsyncThunk<EcartDto, { instance: any; a: string; b: string; raison: 'ecarte' | 'distingue' }, { rejectValue: EchecRapprochement }>(
    'rapprochements/ecarter',
    async ({ instance, a, b, raison }, { rejectWithValue }) => {
        try {
            return await rapprochementService.ecarter({ a, b, raison }, await tokenOf(instance));
        } catch (error) {
            return rejectWithValue(echecDe(error, 'Enregistrement impossible. Réessayez.'));
        }
    },
);

export const reprendrePaire = createAsyncThunk<{ a: string; b: string }, { instance: any; a: string; b: string }, { rejectValue: EchecRapprochement }>(
    'rapprochements/reprendre',
    async ({ instance, a, b }, { rejectWithValue }) => {
        try {
            await rapprochementService.reprendre(a, b, await tokenOf(instance));
            return { a, b };
        } catch (error) {
            return rejectWithValue(echecDe(error, 'Retrait impossible. Réessayez.'));
        }
    },
);

/** Calcule les empreintes manquantes (100 par appel, compté comme une indexation) puis relit les paires. */
export const calculerEmpreintes = createAsyncThunk<{ calculees: number; restantes: number }, { instance: any; portee: PorteeRapprochement }, { rejectValue: EchecRapprochement }>(
    'rapprochements/calculerEmpreintes',
    async ({ instance, portee }, { dispatch, rejectWithValue }) => {
        try {
            const compte = await rapprochementService.calculerEmpreintes(portee, await tokenOf(instance));
            void dispatch(listerRapprochements({ instance, portee }));
            return compte;
        } catch (error) {
            return rejectWithValue(echecDe(error, 'Calcul impossible. Réessayez.'));
        }
    },
);

/** C31 SP2 : l'aperçu en chiffres d'une réunion ; n'écrit rien, ni au serveur ni dans l'état. */
export const apercuReunion = createAsyncThunk<ApercuReunion, { instance: any; conserveId: string; reuniId: string; dossierId?: string }, { rejectValue: EchecRapprochement }>(
    'rapprochements/apercuReunion',
    async ({ instance, conserveId, reuniId, dossierId }, { rejectWithValue }) => {
        try {
            const jeton = await tokenOf(instance);
            return dossierId
                ? await rapprochementService.apercuReunion(conserveId, reuniId, jeton, dossierId)
                : await rapprochementService.apercuReunion(conserveId, reuniId, jeton);
        } catch (error) {
            return rejectWithValue(echecDe(error, 'Lecture impossible. Réessayez.'));
        }
    },
);

/** C31 SP2 : réunit `reuniId` dans `conserveId`. Le même appel se rejoue après un 409 `REUNION_CONFLIT`. */
export const reunirPaire = createAsyncThunk<BilanReunion, { instance: any; conserveId: string; reuniId: string; definition?: string; dossierId?: string }, { rejectValue: EchecRapprochement }>(
    'rapprochements/reunir',
    async ({ instance, conserveId, reuniId, definition, dossierId }, { rejectWithValue }) => {
        try {
            const corps = { reuniId, ...(definition === undefined ? {} : { definition }), ...(dossierId === undefined ? {} : { dossierId }) };
            return await rapprochementService.reunir(conserveId, corps, await tokenOf(instance));
        } catch (error) {
            return rejectWithValue(echecDe(error, 'Réunion impossible. Réessayez.'));
        }
    },
);

/**
 * C31 bis SP3 : scinde `entreeId` — fait naître un concept plus précis et y déplace les passages
 * cochés. Le même appel se rejoue après un 409 `SCISSION_CONFLIT`. La tranche ne garde rien : le
 * bilan est rendu à la fenêtre, qui relit concepts, vocabulaires, notes et voisins.
 */
export const scinderConcept = createAsyncThunk<BilanScission, { instance: any; entreeId: string; nom: string; definition?: string; passages: PassageAScinder[] }, { rejectValue: EchecRapprochement }>(
    'rapprochements/scinder',
    async ({ instance, entreeId, nom, definition, passages }, { rejectWithValue }) => {
        try {
            const corps = { nom, ...(definition === undefined ? {} : { definition }), passages };
            return await rapprochementService.scinder(entreeId, corps, await tokenOf(instance));
        } catch (error) {
            return rejectWithValue(echecDe(error, 'Scission impossible. Réessayez.'));
        }
    },
);

/** C31 SP3 : les voisins d'un concept de la personne, sur sa fiche ; n'écrit rien. */
export const listerVoisins = createAsyncThunk<ResultatVoisins, { instance: any; entreeId: string }, { rejectValue: EchecRapprochement }>(
    'rapprochements/listerVoisins',
    async ({ instance, entreeId }, { rejectWithValue }) => {
        try {
            return await rapprochementService.voisins(entreeId, await tokenOf(instance));
        } catch (error) {
            return rejectWithValue(echecDe(error, 'Lecture impossible. Réessayez.'));
        }
    },
);

/** Les voisins sans `id` (le voisin écarté ou réuni, ou tout si le concept lui-même a disparu). */
function retirerDesVoisins(state: RapprochementsState, id: string, disparu: string | null): void {
    if (!state.voisins) return;
    if (disparu && state.voisins.concept.id === disparu) { state.voisins = null; return; }
    state.voisins.voisins = state.voisins.voisins.filter(v => v.concept.id !== id);
}

const rapprochementsSlice = createSlice({
    name: 'rapprochements',
    initialState: initialRapprochementsState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(listerRapprochements.pending, (state, action) => {
                state.status = 'loading';
                state.portee = action.meta.arg.portee;
                state.requestId = action.meta.requestId;
                delete state.erreur;
                delete state.erreurCode;
            })
            .addCase(listerRapprochements.fulfilled, (state, action) => {
                if (state.requestId !== action.meta.requestId) return;
                state.resultat = action.payload;
                state.status = 'ready';
            })
            .addCase(listerRapprochements.rejected, (state, action) => {
                if (state.requestId !== action.meta.requestId) return;
                state.status = 'error';
                state.resultat = null;
                state.erreur = action.payload?.message ?? 'Lecture impossible. Réessayez.';
                if (action.payload?.code) state.erreurCode = action.payload.code;
            })
            .addCase(listerEcarts.fulfilled, (state, action) => {
                state.ecarts = action.payload;
            })
            .addCase(ecarterPaire.fulfilled, (state, action) => {
                const ecart = action.payload;
                const cle = cleDuCouple(ecart.a.id, ecart.b.id);
                if (state.resultat) {
                    state.resultat.paires = state.resultat.paires.filter(p => cleDuCouple(p.a.id, p.b.id) !== cle);
                }
                state.ecarts = [ecart, ...state.ecarts.filter(e => cleDuCouple(e.a.id, e.b.id) !== cle)];
                // Sur la fiche : le voisin écarté (ou distingué) quitte la liste.
                if (state.voisins) {
                    const autre = state.voisins.concept.id === ecart.a.id ? ecart.b.id : state.voisins.concept.id === ecart.b.id ? ecart.a.id : null;
                    if (autre) retirerDesVoisins(state, autre, null);
                }
            })
            .addCase(reprendrePaire.fulfilled, (state, action) => {
                const cle = cleDuCouple(action.payload.a, action.payload.b);
                state.ecarts = state.ecarts.filter(e => cleDuCouple(e.a.id, e.b.id) !== cle);
            })
            .addCase(reunirPaire.fulfilled, (state, action) => {
                const bilan = action.payload;
                state.dernierBilan = bilan;
                // Par dossier (C31 bis SP2) : A survit, rien ne quitte la liste ici (les paires sont relues).
                if (bilan.reuni.survit) return;
                if (state.resultat) {
                    // A a disparu : toute paire qui le porte, pas seulement celle réunie.
                    state.resultat.paires = state.resultat.paires.filter(p => p.a.id !== bilan.reuni.id && p.b.id !== bilan.reuni.id);
                }
                retirerDesVoisins(state, bilan.reuni.id, bilan.reuni.id);
            })
            .addCase(listerVoisins.pending, (state, action) => {
                state.voisinsRequestId = action.meta.requestId;
                state.voisins = null;
            })
            .addCase(listerVoisins.fulfilled, (state, action) => {
                if (state.voisinsRequestId !== action.meta.requestId) return;
                state.voisins = action.payload;
            })
            .addCase(listerVoisins.rejected, (state, action) => {
                if (state.voisinsRequestId !== action.meta.requestId) return;
                state.voisins = null;
            })
            .addCase(calculerEmpreintes.pending, (state) => {
                state.calculEnCours = true;
            })
            .addCase(calculerEmpreintes.fulfilled, (state) => {
                state.calculEnCours = false;
            })
            .addCase(calculerEmpreintes.rejected, (state, action) => {
                state.calculEnCours = false;
                state.erreur = action.payload?.message ?? 'Calcul impossible. Réessayez.';
            });
    },
});

export default rapprochementsSlice.reducer;

// --- Sélecteurs (tolérants : un store partiel rend des valeurs stables) ---
export const selectRapprochementsStatus = (state: any): RapprochementsStatus => state?.rapprochements?.status ?? 'idle';
export const selectRapprochementsPortee = (state: any): PorteeRapprochement => state?.rapprochements?.portee ?? PORTEE_PAR_DEFAUT;
export const selectRapprochementsResultat = (state: any): ResultatRapprochements | null => state?.rapprochements?.resultat ?? null;
export const selectRapprochementsEcarts = (state: any): EcartDto[] => state?.rapprochements?.ecarts ?? AUCUN_ECART;
export const selectRapprochementsErreur = (state: any): string | undefined => state?.rapprochements?.erreur;
export const selectRapprochementsErreurCode = (state: any): string | undefined => state?.rapprochements?.erreurCode;
export const selectCalculEnCours = (state: any): boolean => state?.rapprochements?.calculEnCours ?? false;
export const selectDernierBilan = (state: any): BilanReunion | null => state?.rapprochements?.dernierBilan ?? null;
/** Les voisins lus pour CE concept, sinon `null` (ceux d'un autre concept ne sont jamais rendus). */
export const selectVoisinsDe = (state: any, entreeId: string | undefined): ResultatVoisins | null => {
    const v: ResultatVoisins | null | undefined = state?.rapprochements?.voisins;
    return v && entreeId && v.concept.id === entreeId ? v : null;
};
